import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { Box, Card, Typography, useMediaQueries } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useStore } from '~/contexts';
import { useSetAuthToken } from '~/hooks/useSetAuthToken';
import { useLocaleKeys } from '~/i18n';
import { queryClient } from '~/queryClient';
import { selectors } from '~/store/state-selectors';
import { useStoreActions } from '~/store/store-context';
import { Accounting } from '~/types/api.model';
import backgroundImage from './background.png';
import { OnboardingLayout } from './components/OnboardingLayout/OnboardingLayout';
import { BrandStep } from './components/steps/BrandStep/BrandStep';
import { computeInitialBranding, computeInitialLogoDataURL } from './components/steps/BrandStep/hooks/useBrandStepForm';
import { BusinessDetailsAStep } from './components/steps/BusinessDetailsAStep/BusinessDetailsAStep';
import { BusinessDetailsBStep } from './components/steps/BusinessDetailsBStep/BusinessDetailsBStep';
import { ClosingStep } from './components/steps/ClosingStep/ClosingStep';
import { IntegrationsStep } from './components/steps/IntegrationsStep/IntegrationsStep';
import { IntentStep } from './components/steps/IntentStep/IntentStep';
import { IntroStep } from './components/steps/IntroStep/IntroStep';
import { createEmptyMember, InviteTeamStep } from './components/steps/InviteTeamStep/InviteTeamStep';
import { QboXeroIntegrationStep } from './components/steps/QboXeroIntegrationStep/QboXeroIntegrationStep';
import { SecureLoginStep } from './components/steps/SecureLoginStep/SecureLoginStep';
import { WaitingStep } from './components/steps/WaitingStep/WaitingStep';
import { WebsiteStep } from './components/steps/WebsiteStep/WebsiteStep';
import { clearPersistedOnboardingProgress, persistLogo, persistProgress, pickInitialStepFromBusinessData, readPersistedLogo, readPersistedProgress, useOnboardingFlow } from './hooks/useOnboardingFlow';
import styles from './VendorOnboarding.module.scss';
const EMPTY_POSTAL_ADDRESS = {
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'US'
};
export const GetVendorOnboardingTID = (parentTestId)=>getTestIds({
        parentTestId
    });
const DEFAULT_BUSINESS_DETAILS_A = {
    companyName: '',
    postalAddress: EMPTY_POSTAL_ADDRESS,
    phoneNumber: ''
};
const DEFAULT_BUSINESS_DETAILS_B = {
    businessDescription: '',
    numInvoices: 'numInvoices20To50',
    teamSize: '2_to_5'
};
const fromDeprecatedPostalAddress = (pa)=>{
    var _pa_addressTwo;
    if (pa == null) return null;
    return {
        address1: pa.addressOne,
        address2: (_pa_addressTwo = pa.addressTwo) !== null && _pa_addressTwo !== void 0 ? _pa_addressTwo : '',
        city: pa.city,
        state: pa.state,
        postalCode: pa.postalCode,
        country: pa.country
    };
};
const computeInitialBusinessDetailsA = (worksAt, phone)=>{
    var _ref, _fromDeprecatedPostalAddress;
    return {
        companyName: (_ref = worksAt === null || worksAt === void 0 ? void 0 : worksAt.name) !== null && _ref !== void 0 ? _ref : DEFAULT_BUSINESS_DETAILS_A.companyName,
        postalAddress: (_fromDeprecatedPostalAddress = fromDeprecatedPostalAddress(worksAt === null || worksAt === void 0 ? void 0 : worksAt.postalAddress)) !== null && _fromDeprecatedPostalAddress !== void 0 ? _fromDeprecatedPostalAddress : DEFAULT_BUSINESS_DETAILS_A.postalAddress,
        phoneNumber: phone !== null && phone !== void 0 ? phone : DEFAULT_BUSINESS_DETAILS_A.phoneNumber
    };
};
const computeInitialBusinessDetailsB = (worksAt)=>{
    var _ref;
    return _object_spread_props(_object_spread({}, DEFAULT_BUSINESS_DETAILS_B), {
        businessDescription: (_ref = worksAt === null || worksAt === void 0 ? void 0 : worksAt.description) !== null && _ref !== void 0 ? _ref : DEFAULT_BUSINESS_DETAILS_B.businessDescription
    });
};
export const VendorOnboarding = (param)=>{
    let { testId, mode, onComplete, onClose } = param;
    var _useStore, _ref, _ref1, _ref2, _ref3, _ref4, _ref5, _ref6, _ref7, _ref8, _ref9, _ref10, _ref11, _ref12, _ref13;
    const testIds = useMemo(()=>GetVendorOnboardingTID(testId), [
        testId
    ]);
    const { t: vendorOnboardingTexts } = useLocaleKeys('vendorOnboarding');
    const worksAt = (_useStore = useStore(selectors.user.business)) !== null && _useStore !== void 0 ? _useStore : null;
    const isUserAlreadyVendor = useStore(selectors.user.isVendor);
    const userInfo = useStore((state)=>state.userInfo);
    const userPhone = userInfo === null || userInfo === void 0 ? void 0 : userInfo.phone();
    const currentUserId = (_ref = userInfo === null || userInfo === void 0 ? void 0 : userInfo.id()) !== null && _ref !== void 0 ? _ref : null;
    const persistedProgress = useMemo(()=>readPersistedProgress(currentUserId), []);
    const persistedFormState = (_ref1 = persistedProgress === null || persistedProgress === void 0 ? void 0 : persistedProgress.formState) !== null && _ref1 !== void 0 ? _ref1 : null;
    const persistedLogo = useMemo(()=>readPersistedLogo(currentUserId), []);
    const existingVendorWorksAt = isUserAlreadyVendor ? worksAt : null;
    const [businessDetailsA, setBusinessDetailsA] = useState((_ref2 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.businessDetailsA) !== null && _ref2 !== void 0 ? _ref2 : computeInitialBusinessDetailsA(existingVendorWorksAt, userPhone));
    const [businessDetailsB, setBusinessDetailsB] = useState((_ref3 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.businessDetailsB) !== null && _ref3 !== void 0 ? _ref3 : computeInitialBusinessDetailsB(existingVendorWorksAt));
    const [intent, setIntent] = useState((_ref4 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.intent) !== null && _ref4 !== void 0 ? _ref4 : {
        purposes: []
    });
    const [inviteTeam, setInviteTeam] = useState((_ref5 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.inviteTeam) !== null && _ref5 !== void 0 ? _ref5 : {
        members: [
            createEmptyMember()
        ]
    });
    const [integrations, setIntegrations] = useState((_ref6 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.integrations) !== null && _ref6 !== void 0 ? _ref6 : {
        selectedTools: [],
        otherTools: []
    });
    const [website, setWebsite] = useState((_ref7 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.website) !== null && _ref7 !== void 0 ? _ref7 : {
        url: ''
    });
    const accountingType = integrations.selectedTools.includes('toolQuickbooksOnline') ? Accounting.QBO : Accounting.Xero;
    const [integrationsSkipped, setIntegrationsSkipped] = useState((_ref8 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.integrationsSkipped) !== null && _ref8 !== void 0 ? _ref8 : false);
    const skipInviteTeam = businessDetailsB.teamSize === 'just_me';
    const skipQboXero = integrationsSkipped || !integrations.selectedTools.includes('toolQuickbooksOnline') && !integrations.selectedTools.includes('toolXero');
    const skipSecureLogin = useStore(selectors.user.hasIdentity);
    const { mobile: isMobile } = useMediaQueries();
    const initialStepOverride = useMemo(()=>pickInitialStepFromBusinessData({
            hasBusiness: existingVendorWorksAt != null,
            hasPostalAddress: (existingVendorWorksAt === null || existingVendorWorksAt === void 0 ? void 0 : existingVendorWorksAt.postalAddress) != null,
            hasDescription: (existingVendorWorksAt === null || existingVendorWorksAt === void 0 ? void 0 : existingVendorWorksAt.description) != null,
            hasIdentity: skipSecureLogin,
            mode: mode !== null && mode !== void 0 ? mode : 'default'
        }), []);
    const { currentStep, progress, isSkippableStep, hideBackButton, goNext, goBack } = useOnboardingFlow({
        mode,
        skipInviteTeam,
        skipQboXero,
        skipSecureLogin,
        initialStepOverride
    });
    const brandStepRef = useRef(null);
    const { logBI, fetchUserInfo } = useStoreActions();
    const setAuthToken = useSetAuthToken();
    const [createdVendorBusinessId, setCreatedVendorBusinessId] = useState((_ref9 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.createdVendorBusinessId) !== null && _ref9 !== void 0 ? _ref9 : null);
    const handleAuthTokenReceived = useCallback(async (authToken, businessId)=>{
        queryClient.clear();
        setCreatedVendorBusinessId(businessId);
        const persisted = readPersistedProgress(currentUserId);
        if ((persisted === null || persisted === void 0 ? void 0 : persisted.formState) != null) {
            persistProgress(_object_spread_props(_object_spread({}, persisted), {
                formState: _object_spread_props(_object_spread({}, persisted.formState), {
                    createdVendorBusinessId: businessId
                })
            }));
        }
        await setAuthToken(authToken);
    }, [
        setAuthToken,
        currentUserId
    ]);
    const handleOnboardingComplete = useCallback(async ()=>{
        await fetchUserInfo().catch(()=>undefined);
        clearPersistedOnboardingProgress();
        onComplete();
    }, [
        fetchUserInfo,
        onComplete
    ]);
    const submitRef = useRef(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isIntegrationConnected, setIsIntegrationConnected] = useState((_ref10 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.isIntegrationConnected) !== null && _ref10 !== void 0 ? _ref10 : false);
    const inCreateVendorMode = mode === 'additionalAccount' || mode === 'clientToVendor';
    const secureLoginCompletesOnboarding = mode === 'inviteeIdentity';
    const shouldUpdateAtStepA = createdVendorBusinessId != null || !inCreateVendorMode && existingVendorWorksAt != null;
    const [creditsAwarded, setCreditsAwarded] = useState(()=>{
        var _ref;
        return (_ref = persistedProgress === null || persistedProgress === void 0 ? void 0 : persistedProgress.creditsAwarded) !== null && _ref !== void 0 ? _ref : false;
    });
    const [direction, setDirection] = useState('forward');
    const [extractedBranding, setExtractedBranding] = useState((_ref11 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.extractedBranding) !== null && _ref11 !== void 0 ? _ref11 : null);
    const [branding, setBrandingState] = useState((_ref12 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.branding) !== null && _ref12 !== void 0 ? _ref12 : computeInitialBranding(worksAt, (_ref13 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.extractedBranding) !== null && _ref13 !== void 0 ? _ref13 : null));
    const [brandLogo, setBrandLogo] = useState(()=>{
        var _ref;
        return {
            file: null,
            asDataURL: persistedLogo !== null && persistedLogo !== void 0 ? persistedLogo : computeInitialLogoDataURL(worksAt, (_ref = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.extractedBranding) !== null && _ref !== void 0 ? _ref : null)
        };
    });
    const isWaitingOrClosing = currentStep === 'waiting' || currentStep === 'closing';
    const isBusinessDetailsStep = currentStep === 'businessDetailsA' || currentStep === 'businessDetailsB';
    const showCloseButton = (()=>{
        if (mode === 'default') return false;
        if (mode === 'clientToVendor' && currentStep === 'intro') return true;
        if (mode === 'additionalAccount') return currentStep === 'businessDetailsA';
        return isBusinessDetailsStep;
    })();
    useEffect(()=>{
        setIsSubmitting(false);
    }, [
        currentStep
    ]);
    useEffect(()=>{
        persistProgress({
            currentStep,
            creditsAwarded,
            formState: {
                businessDetailsA,
                businessDetailsB,
                intent,
                integrations,
                inviteTeam,
                website,
                integrationsSkipped,
                isIntegrationConnected,
                extractedBranding,
                branding,
                createdVendorBusinessId
            },
            userId: currentUserId !== null && currentUserId !== void 0 ? currentUserId : undefined
        });
    }, [
        currentStep,
        creditsAwarded,
        businessDetailsA,
        businessDetailsB,
        intent,
        integrations,
        inviteTeam,
        website,
        integrationsSkipped,
        isIntegrationConnected,
        extractedBranding,
        branding,
        createdVendorBusinessId,
        currentUserId
    ]);
    useEffect(()=>{
        persistLogo(brandLogo.asDataURL, currentUserId);
    }, [
        brandLogo.asDataURL,
        currentUserId
    ]);
    const goForward = useCallback(()=>{
        setDirection('forward');
        setTimeout(()=>goNext(), 0);
    }, [
        goNext
    ]);
    const goBackward = useCallback(()=>{
        setDirection('back');
        setTimeout(()=>goBack(), 0);
    }, [
        goBack
    ]);
    const stepConfig = useMemo(()=>({
            businessDetailsA: {
                title: vendorOnboardingTexts.businessDetailsAStep.title(),
                subtitle: vendorOnboardingTexts.businessDetailsAStep.subtitle()
            },
            businessDetailsB: {
                title: vendorOnboardingTexts.businessDetailsBStep.title(),
                subtitle: vendorOnboardingTexts.businessDetailsBStep.subtitle()
            },
            brand: {},
            intent: {
                title: vendorOnboardingTexts.intentStep.title(),
                subtitle: vendorOnboardingTexts.intentStep.subtitle()
            },
            integrations: {
                title: vendorOnboardingTexts.integrationsStep.title(),
                subtitle: vendorOnboardingTexts.integrationsStep.subtitle()
            },
            inviteTeam: {
                title: vendorOnboardingTexts.inviteTeamStep.title(),
                subtitle: vendorOnboardingTexts.inviteTeamStep.subtitle()
            },
            businessWebsite: {
                title: vendorOnboardingTexts.websiteStep.title(),
                subtitle: vendorOnboardingTexts.websiteStep.subtitle()
            },
            qboXeroIntegration: {
                title: accountingType === Accounting.QBO ? vendorOnboardingTexts.integrationConnectionStep.titleQbo() : vendorOnboardingTexts.integrationConnectionStep.titleXero(),
                subtitle: vendorOnboardingTexts.integrationConnectionStep.subtitle()
            },
            secureLogin: {
                title: vendorOnboardingTexts.secureLoginStep.title(),
                subtitle: vendorOnboardingTexts.secureLoginStep.subtitle()
            }
        }), [
        accountingType
    ]);
    const handleIntroWatched = ()=>{
        setCreditsAwarded(true);
        goForward();
    };
    const handleLayoutContinue = ()=>{
        if (currentStep === 'integrations') {
            setIntegrationsSkipped(false);
        }
        if (currentStep === 'brand') {
            if (isSubmitting) return;
            setIsSubmitting(true);
            void (async ()=>{
                try {
                    var _ref;
                    var _brandStepRef_current;
                    const saved = (_ref = await ((_brandStepRef_current = brandStepRef.current) === null || _brandStepRef_current === void 0 ? void 0 : _brandStepRef_current.save())) !== null && _ref !== void 0 ? _ref : true;
                    if (!saved) {
                        setIsSubmitting(false);
                        return;
                    }
                    goForward();
                } catch (unused) {
                    setIsSubmitting(false);
                }
            })();
            return;
        }
        const handle = submitRef.current;
        if (handle != null) {
            void handle.submit();
            return;
        }
        goForward();
    };
    const handleLayoutSkip = ()=>{
        logBI(99999, {
            step: currentStep,
            action: 'skip'
        });
        if (currentStep === 'integrations') {
            setIntegrationsSkipped(true);
        }
        goForward();
    };
    const handleWebsiteContinue = (extracted)=>{
        setExtractedBranding(extracted);
        setBrandingState(computeInitialBranding(worksAt, extracted));
        setBrandLogo({
            file: null,
            asDataURL: computeInitialLogoDataURL(worksAt, extracted)
        });
        goForward();
    };
    const renderStepContent = ()=>{
        switch(currentStep){
            case 'businessDetailsA':
                return /*#__PURE__*/ _jsx(BusinessDetailsAStep, {
                    values: businessDetailsA,
                    onChange: setBusinessDetailsA,
                    onContinue: goForward,
                    submitRef: submitRef,
                    onSubmittingChange: setIsSubmitting,
                    shouldUpdate: shouldUpdateAtStepA,
                    onAuthTokenReceived: handleAuthTokenReceived,
                    entitleOnboardingVideoCredit: creditsAwarded
                });
            case 'businessDetailsB':
                var _ref;
                return /*#__PURE__*/ _jsx(BusinessDetailsBStep, {
                    values: businessDetailsB,
                    onChange: setBusinessDetailsB,
                    onContinue: goForward,
                    submitRef: submitRef,
                    onSubmittingChange: setIsSubmitting,
                    businessId: (_ref = worksAt === null || worksAt === void 0 ? void 0 : worksAt.id) !== null && _ref !== void 0 ? _ref : null
                });
            case 'intent':
                return /*#__PURE__*/ _jsx(IntentStep, {
                    values: intent,
                    onChange: setIntent,
                    onContinue: goForward,
                    submitRef: submitRef
                });
            case 'integrations':
                return /*#__PURE__*/ _jsx(IntegrationsStep, {
                    values: integrations,
                    onChange: setIntegrations,
                    onContinue: goForward,
                    submitRef: submitRef
                });
            case 'inviteTeam':
                return /*#__PURE__*/ _jsx(InviteTeamStep, {
                    values: inviteTeam,
                    onChange: setInviteTeam,
                    onContinue: goForward,
                    submitRef: submitRef,
                    onSubmittingChange: setIsSubmitting,
                    targetBusinessId: createdVendorBusinessId !== null && createdVendorBusinessId !== void 0 ? createdVendorBusinessId : undefined
                });
            case 'businessWebsite':
                return /*#__PURE__*/ _jsx(WebsiteStep, {
                    values: website,
                    onChange: setWebsite,
                    onContinue: handleWebsiteContinue,
                    onSubmittingChange: setIsSubmitting,
                    submitRef: submitRef
                });
            case 'qboXeroIntegration':
                return /*#__PURE__*/ _jsx(QboXeroIntegrationStep, {
                    accountingType: accountingType,
                    connected: isIntegrationConnected,
                    onContinue: goForward,
                    onConnectingChange: setIsSubmitting,
                    onConnectedChange: setIsIntegrationConnected,
                    submitRef: submitRef
                });
            case 'brand':
                return /*#__PURE__*/ _jsx(BrandStep, {
                    ref: brandStepRef,
                    worksAt: worksAt,
                    branding: branding,
                    onBrandingChange: setBrandingState,
                    logo: brandLogo,
                    onLogoChange: setBrandLogo
                });
            case 'secureLogin':
                return /*#__PURE__*/ _jsx(SecureLoginStep, {
                    onContinue: secureLoginCompletesOnboarding ? handleOnboardingComplete : goForward,
                    onSubmittingChange: setIsSubmitting,
                    submitRef: submitRef
                });
            default:
                return null;
        }
    };
    const config = stepConfig[currentStep];
    return /*#__PURE__*/ _jsx(Box, {
        className: clsx(styles.root, {
            [styles.mobile]: isMobile,
            [styles.dashboardBackground]: isWaitingOrClosing
        }),
        style: {
            '--vob-bg-image': "url(".concat(backgroundImage, ")")
        },
        testId: testIds.VendorOnboardingTestIds.Root(),
        children: /*#__PURE__*/ _jsxs(Card, {
            bi: {
                name: 'vendor_onboarding_card'
            },
            className: styles.card,
            borderSkin: "default",
            borderRadius: "m",
            children: [
                currentStep === 'intro' && /*#__PURE__*/ _jsx(IntroStep, {
                    onSkip: goForward,
                    onComplete: handleIntroWatched,
                    onClose: showCloseButton ? onClose : undefined,
                    creditsAlreadyAwarded: creditsAwarded
                }),
                currentStep === 'waiting' && /*#__PURE__*/ _jsx(WaitingStep, {
                    onComplete: goNext
                }),
                currentStep === 'closing' && /*#__PURE__*/ _jsx(ClosingStep, {
                    onComplete: handleOnboardingComplete
                }),
                currentStep !== 'intro' && currentStep !== 'waiting' && currentStep !== 'closing' && /*#__PURE__*/ _jsx(OnboardingLayout, {
                    progress: progress,
                    onBack: hideBackButton ? undefined : goBackward,
                    onContinue: handleLayoutContinue,
                    onSkip: handleLayoutSkip,
                    onClose: showCloseButton ? onClose : undefined,
                    showSkipButton: isSkippableStep || currentStep === 'qboXeroIntegration' && !isIntegrationConnected,
                    continueLabel: currentStep === 'qboXeroIntegration' && !isIntegrationConnected ? accountingType === Accounting.QBO ? vendorOnboardingTexts.integrationConnectionStep.connectQbo() : vendorOnboardingTexts.integrationConnectionStep.connectXero() : undefined,
                    skipLabel: currentStep === 'qboXeroIntegration' && !isIntegrationConnected ? vendorOnboardingTexts.integrationConnectionStep.connectLater() : undefined,
                    isContinueDisabled: isSubmitting,
                    isContinueLoading: isSubmitting,
                    isBackDisabled: isSubmitting || mode === 'additionalAccount' && currentStep === 'integrations',
                    children: /*#__PURE__*/ _jsx(AnimatePresence, {
                        mode: "wait",
                        custom: direction,
                        initial: false,
                        children: /*#__PURE__*/ _jsxs(motion.div, {
                            custom: direction,
                            initial: (dir)=>({
                                    x: dir === 'forward' ? 50 : -50,
                                    opacity: 0
                                }),
                            animate: {
                                x: 0,
                                opacity: 1
                            },
                            exit: (dir)=>({
                                    x: dir === 'forward' ? -50 : 50,
                                    opacity: 0
                                }),
                            transition: {
                                duration: 0.4,
                                ease: 'easeInOut'
                            },
                            className: clsx(styles.animatedStep, currentStep === 'brand' && styles.fullWidthStep, currentStep === 'inviteTeam' && styles.topAligned),
                            children: [
                                ((config === null || config === void 0 ? void 0 : config.title) || (config === null || config === void 0 ? void 0 : config.subtitle)) && /*#__PURE__*/ _jsxs("div", {
                                    className: styles.titleBlock,
                                    children: [
                                        (config === null || config === void 0 ? void 0 : config.title) && /*#__PURE__*/ _jsx(Typography, {
                                            variant: "h5",
                                            weight: "normal",
                                            className: styles.title,
                                            children: config.title
                                        }),
                                        (config === null || config === void 0 ? void 0 : config.subtitle) && /*#__PURE__*/ _jsx(Typography, {
                                            variant: "paragraph1",
                                            skin: "secondary",
                                            children: config.subtitle
                                        })
                                    ]
                                }),
                                renderStepContent()
                            ]
                        }, currentStep)
                    })
                })
            ]
        })
    });
};
